import { onFormatDate, YesNoIcon } from 'components'
import {
  ColumnRenderer,
  EntityList,
  EntityListProps,
  renderCopyable,
  renderInvertedYesNoIcon,
  renderPath,
  renderYesNoIcon,
} from 'components/EntityList'
import { Resource } from 'model'
import { IColumn, SelectionMode } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'

export type ResourceListProps = EntityListProps<Resource> & {
  category: string
  type: string
}

export const ResourceList: React.FunctionComponent<ResourceListProps> = ({
  items,
  type,
  category,
  ...props
}) => {
  const { t } = useTranslation()

  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const renderResourceType = React.useMemo<ColumnRenderer>(
    () => (item, i, col) => t(`field.type.${item[col!.fieldName!]}`),
    [t]
  )

  const columns = React.useMemo(
    () =>
      [
        fieldColumn('inactive', {
          minWidth: 40,
          maxWidth: 40,
          name: t('field.active.label'),
          onRender: renderInvertedYesNoIcon,
          ungroupable: true,
        }),

        fieldColumn('title', {
          minWidth: 125,
          isSorted: true,

          ungroupable: true,
        }),
        type === 'all' &&
          fieldColumn('type', {
            minWidth: 100,
            onRender: renderResourceType,
          }),

        ...(category === 'fleet'
          ? [
              fieldColumn('color', {
                minWidth: 75,
                ungroupable: true,
              }),
              fieldColumn('licensePlate', {
                minWidth: 85,
                ungroupable: true,
              }),
              fieldColumn('chassisNumber', {
                minWidth: 100,
                onRender: renderCopyable(),
                ungroupable: true,
              }),
              fieldColumn('tuevSweden', {
                minWidth: 50,
                onRender: renderYesNoIcon,
                ungroupable: true,
              }),
              fieldColumn('tuevUntil', {
                minWidth: 75,
                ungroupable: true,
              }),
            ]
          : []),
        category === 'coldCell' &&
          fieldColumn('location', {
            minWidth: 85,
            ungroupable: true,
          }),

        ...(category.match(/(building|ferryConnection|coldCell|refuelling)/i)
          ? []
          : [
              fieldColumn('parent.title', {
                minWidth: 125,
                onRender: renderPath,
                ungroupable: true,
              }),
            ]),

        ...(category === 'ferryConnection'
          ? [
              fieldColumn('origin', {
                minWidth: 125,
                ungroupable: true,
              }),
              fieldColumn('destination', {
                minWidth: 125,
                ungroupable: true,
              }),
            ]
          : []),
        ...(category === 'lodging'
          ? [
              fieldColumn('address', {
                ungroupable: true,
              }),
              fieldColumn('place', {
                ungroupable: true,
              }),
              fieldColumn('land', {
                ungroupable: true,
              }),
              fieldColumn('phone', {
                ungroupable: true,
              }),
              fieldColumn('fax', {
                ungroupable: true,
              }),
              fieldColumn('contact', {
                ungroupable: true,
              }),
              fieldColumn('email', {
                ungroupable: true,
              }),
              fieldColumn('emailHRG', {
                ungroupable: true,
              }),
            ]
          : []),
        ...(category.match(
          /(building|office|testing|ferryConnection|refuelling|coldCell|accessory|workshop)/i
        )
          ? [
              fieldColumn('unit', {
                minWidth: 75,
                ungroupable: true,
              }),
              fieldColumn('costPerUnit', {
                minWidth: 75,
                ungroupable: true,
              }),
            ]
          : []),
        ...(category.match(/(testing)/i)
          ? [
              fieldColumn('notification', {
                minWidth: 125,
                ungroupable: true,
              }),
            ]
          : []),
        fieldColumn('annotation', {
          minWidth: 125,
          ungroupable: true,
        }),
        fieldColumn('usageTimespans', {
          minWidth: 40,
          maxWidth: 40,
          name: t('field.external.label'),
          onRender: (item: Resource) => {
            if (!item.usageTimespans || item.usageTimespans.length === 0) {
              return <YesNoIcon value={false} />
            }
            return (
              <>
                {item.usageTimespans.map((uts) => {
                  return (
                    <>
                      {onFormatDate(new Date(uts.dateFrom))}
                      {' - '}
                      {onFormatDate(new Date(uts.dateTo))}
                      <br />
                    </>
                  )
                })}
              </>
            )
          },
          ungroupable: true,
        }),
      ].filter(Boolean) as IColumn[],
    [t, fieldColumn, type, category, renderResourceType]
  )

  return (
    <EntityList
      alternate={true}
      selectionMode={SelectionMode.multiple}
      stickyHeader={true}
      items={items}
      columns={columns}
      {...props}
    />
  )
}
